import { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle } from "lucide-react";
import { hardenedSubmit, generateConfirmation } from "@/lib/submit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Application Confirmation Screen (6-second hold then redirect) ── */
function ConfirmationScreen({ confirmationNumber, name, onComplete }) {
  const [countdown, setCountdown] = useState(6);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((p) => {
        if (p <= 1) { clearInterval(timer); onComplete(); return 0; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [onComplete]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 bg-[#0a0e1a]" data-testid="application-confirmation-screen">
      <div className="max-w-md w-full">
        <div className="rounded-xl border border-slate-700/50 bg-[#111827]/70 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Application Submitted!</h2>
          <p className="text-sm text-slate-400 mb-6">
            Thank you, {name}. Your application has been received and is being processed.
          </p>

          <div className="rounded-lg border border-amber-500/30 bg-[#0f1629] p-4 mb-6">
            <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">Application Confirmation Number</p>
            <p className="text-xl font-mono font-bold text-amber-400" data-testid="app-confirmation-number">{confirmationNumber}</p>
          </div>

          <p className="text-sm text-slate-400 mb-4">
            Redirecting to ACH authorization in <span className="text-amber-400 font-bold">{countdown}</span> seconds...
          </p>

          <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-1000 ease-linear"
              style={{ width: `${((6 - countdown) / 6) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Shared styles ── */
const inputCls = "w-full h-11 px-3 rounded-md border border-amber-500/30 bg-[#0f1629] text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40 transition-colors";
const selectCls = "w-full h-11 px-3 rounded-md border border-amber-500/30 bg-[#0f1629] text-white text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40 appearance-none transition-colors";
const labelCls = "block text-[11px] font-bold text-amber-400/90 uppercase tracking-wider mb-1.5";

/* ── Fee Summary ── */
function FeeSummary({ loanAmount }) {
  const num = parseFloat((loanAmount || "0").replace(/[^0-9.]/g, ""));
  const principal = isNaN(num) || num <= 0 ? 0 : num;
  const fee = principal * 0.10;
  const total = principal + fee;
  const fmt = (v) => v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div data-testid="fee-summary" className="mt-5 rounded-lg border border-amber-500/30 bg-[#111827]/80 p-5">
      <h3 className="text-xs font-extrabold text-amber-400 uppercase tracking-widest text-center mb-4">Fee Summary</h3>

      <div className="flex items-center justify-between text-sm mb-2">
        <span className="text-slate-400">Desired Loan Amount:</span>
        <span className="text-white font-semibold" data-testid="fee-principal">${fmt(principal)}</span>
      </div>
      <div className="flex items-center justify-between text-sm mb-3">
        <span className="text-slate-400">Platform Service Fee (10%):</span>
        <span className="text-amber-400 font-semibold" data-testid="fee-amount">${fmt(fee)}</span>
      </div>

      <div className="space-y-2 mb-4">
        <div className="px-3 py-2 rounded border border-amber-500/15 bg-[#0f1629]/60">
          <p className="text-xs text-slate-400 leading-relaxed">
            <span className="font-bold text-amber-400">Budget Friendly:</span> To make our expedited service accessible, this one-time platform fee is structured to have a minimal impact on your monthly budget—typically adding only a few dollars to your scheduled payments.
          </p>
        </div>
        <div className="px-3 py-2 rounded border border-amber-500/15 bg-[#0f1629]/60">
          <p className="text-xs text-slate-400 leading-relaxed">
            <span className="font-bold text-amber-400">Transparency Note:</span> This is a success-based service fee, not an interest rate. It is only processed after your loan is successfully deposited.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm pt-3 border-t border-slate-700">
        <span className="font-bold text-white">Total To Be Repaid (Principal + Fee):</span>
        <span className="text-amber-400 font-extrabold text-base" data-testid="fee-total">${fmt(total)}</span>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function ApplyConsumerPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [applicantName, setApplicantName] = useState("");
  const [submitError, setSubmitError] = useState("");

  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", street_address: "", city: "", state: "",
    zip_code: "", ssn: "", date_of_birth: "", monthly_income: "",
    employment_status: "", income_type: "", residence_type: "", desired_loan_amount: "",
    loan_purpose: "", rep_name: "", rep_company: "",
    company_website: "",
  });

  const set = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleLenderComplete = useCallback(() => { navigate("/ach-consumer"); }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    const clientConf = generateConfirmation();
    try {
      setSubmitError("");
      const payload = {
        business_name: form.full_name || "N/A",
        contact_name: form.full_name,
        email: form.email,
        phone: form.phone,
        amount_requested: form.desired_loan_amount || "Not specified",
        street_address: form.street_address,
        city: form.city,
        state: form.state,
        zip_code: form.zip_code,
        ssn: form.ssn,
        date_of_birth: form.date_of_birth,
        monthly_income: form.monthly_income,
        employment_status: form.employment_status,
        income_type: form.income_type,
        residence_type: form.residence_type,
        loan_purpose: form.loan_purpose,
        rep_name: form.rep_name,
        rep_company: form.rep_company,
        company_website: form.company_website,
        _client_confirmation: clientConf,
      };

      const result = await hardenedSubmit("/apply", payload);
      const confNum = result.confirmation_number;
      sessionStorage.setItem("confirmation_number", confNum);
      sessionStorage.setItem("name", form.full_name);
      sessionStorage.setItem("email", form.email);
      sessionStorage.setItem("phone", form.phone);
      sessionStorage.setItem("totalamount", form.desired_loan_amount);
      sessionStorage.setItem("business_name", form.full_name);
      setConfirmationNumber(confNum);
      setApplicantName(form.full_name);
      setShowConfirmation(true);
      toast.success("Application submitted successfully!");
    } catch (err) {
      setSubmitError(err.message || "Submission failed. Your data has NOT been lost. Please try again.");
      toast.error(err.message || "Submission failed. Please try again.");
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  if (showConfirmation) {
    return <ConfirmationScreen confirmationNumber={confirmationNumber} name={applicantName} onComplete={handleLenderComplete} />;
  }

  return (
    <div className="py-10 md:py-14 px-4 bg-consumer min-h-screen" data-testid="apply-consumer-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <Link to="/apply" className="inline-flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300 mb-4" data-testid="back-to-selection">
            &larr; Back to Selection
          </Link>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-2" data-testid="page-title">
            Consumer Credit Application
          </h1>
          <p className="text-sm text-slate-400">
            Complete the form below to explore your financing options. This will not impact your credit score.
          </p>
        </div>

        {/* Soft pull notice */}
        <div data-testid="soft-pull-notice" className="mb-8 px-5 py-3 rounded-md border border-amber-500/30 bg-amber-500/5 text-center">
          <p className="text-sm text-amber-300 font-medium">
            This is a soft pull and will NOT result in a new inquiry or harm your credit score.
          </p>
        </div>

        {/* Form Card */}
        <div className="rounded-xl border border-slate-700/50 bg-[#111827]/70 backdrop-blur-sm p-6 sm:p-8">
          <form onSubmit={handleSubmit} data-testid="apply-consumer-form">
            {/* Honeypot */}
            <div className="hp-field" aria-hidden="true">
              <input type="text" name="company_website" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={set} />
            </div>

            {/* ── Personal Information ── */}
            <h2 className="text-lg font-bold text-amber-400 italic mb-5" data-testid="section-personal">Personal Information</h2>

            <div className="space-y-4">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input data-testid="field-full-name" className={inputCls} name="full_name" required value={form.full_name} onChange={set} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Email Address *</label>
                  <input data-testid="field-email" className={inputCls} name="email" type="email" required value={form.email} onChange={set} />
                </div>
                <div>
                  <label className={labelCls}>Phone Number *</label>
                  <input data-testid="field-phone" className={inputCls} name="phone" type="tel" required value={form.phone} onChange={set} />
                </div>
              </div>

              <div>
                <label className={labelCls}>Street Address *</label>
                <input data-testid="field-street" className={inputCls} name="street_address" required value={form.street_address} onChange={set} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>City *</label>
                  <input data-testid="field-city" className={inputCls} name="city" required value={form.city} onChange={set} />
                </div>
                <div>
                  <label className={labelCls}>State *</label>
                  <input data-testid="field-state" className={inputCls} name="state" required value={form.state} onChange={set} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Zip Code *</label>
                  <input data-testid="field-zip" className={inputCls} name="zip_code" required value={form.zip_code} onChange={set} />
                </div>
                <div className="hidden sm:block" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Social Security Number *</label>
                  <input data-testid="field-ssn" className={inputCls} name="ssn" required value={form.ssn} onChange={set} />
                </div>
                <div>
                  <label className={labelCls}>Date of Birth *</label>
                  <input data-testid="field-dob" className={`${inputCls} date-input`} name="date_of_birth" type="date" required value={form.date_of_birth} onChange={set} />
                </div>
              </div>
            </div>

            {/* ── Financial Information ── */}
            <h2 className="text-lg font-bold text-amber-400 italic mt-8 mb-5" data-testid="section-financial">Financial Information</h2>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Monthly Income *</label>
                  <input data-testid="field-monthly-income" className={inputCls} name="monthly_income" required value={form.monthly_income} onChange={set} />
                </div>
                <div>
                  <label className={labelCls}>Employment Status *</label>
                  <select data-testid="field-employment-status" className={selectCls} name="employment_status" required value={form.employment_status} onChange={set}>
                    <option value="">Select Status</option>
                    <option value="employed">Employed</option>
                    <option value="self_employed">Self-Employed</option>
                    <option value="retired">Retired</option>
                    <option value="unemployed">Unemployed</option>
                    <option value="student">Student</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Income Type *</label>
                  <select data-testid="field-income-type" className={selectCls} name="income_type" required value={form.income_type} onChange={set}>
                    <option value="">Select Income Type</option>
                    <option value="job_income">Job Income</option>
                    <option value="self_employed">Self-Employed</option>
                    <option value="retirement">Retirement</option>
                    <option value="pension">Pension</option>
                    <option value="social_security">Social Security</option>
                    <option value="disability">Disability</option>
                    <option value="unemployment">Unemployment</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Residence Type *</label>
                  <select data-testid="field-residence-type" className={selectCls} name="residence_type" required value={form.residence_type} onChange={set}>
                    <option value="">Select Type</option>
                    <option value="own">Own</option>
                    <option value="rent">Rent</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Desired Loan Amount *</label>
                  <input data-testid="field-loan-amount" className={inputCls} name="desired_loan_amount" required value={form.desired_loan_amount} onChange={set} />
                </div>
              </div>

              {/* Fee Summary */}
              <FeeSummary loanAmount={form.desired_loan_amount} />
            </div>

            {/* ── Loan Purpose ── */}
            <div className="mt-6">
              <label className={labelCls}>Loan Purpose *</label>
              <input data-testid="field-loan-purpose" className={inputCls} name="loan_purpose" required value={form.loan_purpose} onChange={set} />
            </div>

            {/* ── How Did You Hear About Us? ── */}
            <h2 className="text-lg font-bold text-amber-400 italic mt-8 mb-2" data-testid="section-referral">How Did You Hear About Us?</h2>

            <div className="space-y-4 mt-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Rep Name</label>
                  <input data-testid="field-rep-name" className={inputCls} name="rep_name" value={form.rep_name} onChange={set} />
                </div>
                <div>
                  <label className={labelCls}>Company Name</label>
                  <input data-testid="field-rep-company" className={inputCls} name="rep_company" value={form.rep_company} onChange={set} />
                </div>
              </div>
            </div>

            {/* Error banner — persistent, visible */}
            {submitError && (
              <div data-testid="submit-error-banner" className="mt-6 mb-4 px-4 py-3 rounded-lg border border-red-500/40 bg-red-500/10">
                <p className="text-sm text-red-400 font-medium">{submitError}</p>
                <p className="text-xs text-red-400/70 mt-1">Your form data is still here. Please try submitting again.</p>
              </div>
            )}

            {/* Security reassurance */}
            <div data-testid="security-banner" className="mt-8 mb-4 flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-500/20 bg-[#151d2e]">
              <svg className="w-4 h-4 text-amber-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <p className="text-xs text-slate-400 leading-relaxed">Your information is encrypted and securely transmitted. We never share your data with third parties.</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="consumer-submit-btn"
              className="w-full h-12 rounded-md bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-[#0a0e1a] font-bold text-sm uppercase tracking-wider transition-all disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</span>
              ) : (
                "SUBMIT APPLICATION"
              )}
            </button>

            <p className="text-center text-xs text-slate-500 mt-4">
              By submitting, you agree to our{" "}
              <Link to="/privacy" className="text-amber-400 hover:underline">Privacy Policy</Link>
              {" "}and{" "}
              <Link to="/terms" className="text-amber-400 hover:underline">Terms of Service</Link>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
